import { API_URL } from '../config/env';
import { getTurnstileToken } from './turnstile.js';

/**
 * Endpoints protected by the backend Turnstile (captcha) gate. Any apiFetch to
 * one of these paths automatically acquires + attaches a fresh `x-turnstile-token`
 * header. Light content/url keys bypass the gate on the backend, but sending the
 * token here is harmless; when no site key is configured the token is null and
 * nothing is sent.
 */
const CAPTCHAED_PATHS = [
  '/api/content/url',
  '/api/academics/language-learning/resource-url',
  '/api/academics/language-learning/resource-urls-batch',
  '/api/academics/language-learning/resource',
  // Gallery media signing: HEAVY keys (video / full-res, only on OSS) are
  // captcha-gated; LIGHT keys on the disk mirror are let through server-side.
  '/api/gallery/media/signed-url',
  '/api/gallery/media/signed-urls',
];

/**
 * Client-side fetch policy + unified fetch client.
 *
 * Two layers live here:
 *  1. The legacy rate-limit / retry-storm guards (kept for backward compat):
 *     parseRetryAfterSec, isRateLimitedStatus, markStreamBlocked,
 *     isStreamBlocked, fetchJsonWithAuthFallback, fetchOnce.
 *  2. The NEW unified client every domain *Api.js module should use instead of
 *     hand-rolling `fetch(...).then(r => r.json())` + bespoke error handling:
 *     apiFetch / apiFetchJson / apiFetchText.
 *
 * Why consolidate: the 70+ call sites duplicated the same ok-check / json /
 * error-message boilerplate (with subtly different messages), had no uniform
 * timeout, and silently swallowed network failures. apiFetch centralises that
 * and surfaces a normalised ApiError everywhere.
 */

const streamBlockedUntil = new Map();

export function parseRetryAfterSec(response) {
  const header = response?.headers?.get?.('Retry-After') ?? response?.headers?.['retry-after'];
  if (header == null) return null;
  const n = Number.parseInt(String(header), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function isRateLimitedStatus(status) {
  return status === 429 || status === 503;
}

/** Block repeated stream URL attempts after 429/503 (key = route family). */
export function markStreamBlocked(key, retryAfterSec = 3600) {
  streamBlockedUntil.set(key, Date.now() + retryAfterSec * 1000);
}

export function isStreamBlocked(key) {
  const until = streamBlockedUntil.get(key) || 0;
  if (Date.now() < until) return true;
  if (until) streamBlockedUntil.delete(key);
  return false;
}

/**
 * Dual-path GET with at most one 404 fallback — never retries on 429/503.
 */
export async function fetchJsonWithAuthFallback(apiGet, pathPrimary, pathFallback, params) {
  const paths = [pathPrimary, pathFallback].filter(Boolean);
  let lastError = null;
  for (let i = 0; i < paths.length; i += 1) {
    try {
      const response = await apiGet(paths[i], { params });
      return response.data;
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (isRateLimitedStatus(status)) {
        const retryAfter = parseRetryAfterSec(error?.response) || 3600;
        throw new Error(
          status === 503
            ? `Service temporarily suspended. Retry after ${retryAfter}s.`
            : `Rate limit exceeded. Retry after ${retryAfter}s.`,
        );
      }
      if (status === 404 && i < paths.length - 1) continue;
      throw error;
    }
  }
  throw lastError;
}

/**
 * Multi-path GET with 404-continue fallback — built on the unified `apiFetch`.
 *
 * Replaces the bespoke per-file `try path A; on 404 try path B` loops in the
 * domain *Api.js modules. Tries each URL in order; a 404 on a non-final path
 * advances to the next URL; a 429/503 surfaces a rate-limit ApiError (never
 * silently swallowed); any other error on the final path throws. Network
 * failures are NOT retried (retryNetwork:0) — matching the original "try the
 * next candidate" semantics, not a storm of re-fetches of the same URL.
 *
 * @param {string[]} urls  ordered candidate URLs (falsy entries dropped)
 * @param {object}   [options]  forwarded to apiFetch (token, headers, credentials…)
 *                note: `rawResponse`/`acceptedStatuses`/`retryNetwork` are fixed
 *                by this helper and must not be overridden by the caller.
 * @returns {Promise<*>} parsed JSON of the first URL that returns 2xx.
 */
export async function fetchJsonWithPathFallback(urls, options = {}) {
  const list = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
  if (list.length === 0) {
    throw new ApiError('fetchJsonWithPathFallback: no URL provided', { status: 0, url: '' });
  }
  let lastError = null;
  for (let i = 0; i < list.length; i += 1) {
    const url = list[i];
    const isFinal = i === list.length - 1;
    try {
      const res = await apiFetch(url, {
        method: 'GET',
        rawResponse: true,
        acceptedStatuses: [404],
        retryNetwork: 0,
        ...options,
      });
      if (res.ok) {
        return await res.json();
      }
      // Only a 404 reaches here (it's the sole accepted non-2xx status).
      if (res.status === 404 && !isFinal) {
        continue; // try the next candidate path
      }
      lastError = new ApiError(`HTTP ${res.status} for ${url}`, {
        status: res.status,
        statusText: res.statusText,
        url,
      });
      lastError.response = res;
      throw lastError;
    } catch (error) {
      // Rate-limit errors are already normalised + surfaced by apiFetch.
      if (error instanceof ApiError && error.isRateLimited) throw error;
      // Any other error (network, 404-on-final, unexpected) on a non-final path
      // advances to the next candidate; on the final path it propagates.
      lastError = error;
      if (!isFinal) continue;
      throw error;
    }
  }
  throw lastError || new ApiError('fetchJsonWithPathFallback: all paths failed', { status: 0 });
}

/** fetch() wrapper — no retry on 429/503; respects Retry-After once. */
export async function fetchOnce(url, options = {}) {
  const res = await fetch(url, options);
  if (isRateLimitedStatus(res.status)) {
    const retryAfter = parseRetryAfterSec(res) || 3600;
    const err = new Error(`HTTP ${res.status} — retry after ${retryAfter}s`);
    err.status = res.status;
    err.retryAfterSec = retryAfter;
    throw err;
  }
  return res;
}

// ─────────────────────────────────────────────────────────────────────────────
// Unified fetch client
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_API_TIMEOUT_MS = 15000;

/**
 * Normalised error for every failed apiFetch call: carries the HTTP status,
 * the request URL, and (for rate limits) a retryAfterSec hint.
 */
export class ApiError extends Error {
  constructor(message, { status, statusText, url, body, retryAfterSec } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.body = body;
    this.retryAfterSec = retryAfterSec;
  }

  get isRateLimited() {
    return this.status === 429 || this.status === 503;
  }
}

let tokenProvider = null;

/** Register a global auth-token provider (e.g. read from a React context / localStorage). */
export function setApiTokenProvider(fn) {
  tokenProvider = fn;
}

/** Resolve the current auth token (explicit arg wins over the global provider). */
export function resolveApiToken(explicitToken) {
  if (explicitToken !== undefined && explicitToken !== null) return explicitToken;
  try {
    if (typeof tokenProvider === 'function') return tokenProvider();
  } catch {
    /* provider errors must never break a request */
  }
  return null;
}

function normaliseBody(body) {
  if (body === undefined || body === null) return undefined;
  if (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof Uint8Array ||
    body instanceof URLSearchParams
  ) {
    return body;
  }
  return JSON.stringify(body);
}

function looksLikeJson(text) {
  const t = text.trim();
  return t.startsWith('{') || t.startsWith('[');
}

/**
 * Append a flat `{ key: value }` query-param object to a URL (axios used to do
 * this transparently via `config.params`). Nullish values are skipped. Keeps an
 * existing `?`/`&` intact. Used by the *Api.js modules when converging off
 * axios (which accepted `params` on GET).
 *
 * @param {string} base
 * @param {object} [params]
 * @returns {string}
 */
export function buildUrl(base, params) {
  if (!params || typeof params !== 'object') return base;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    usp.append(k, String(v));
  }
  const qs = usp.toString();
  if (!qs) return base;
  return base + (base.includes('?') ? '&' : '?') + qs;
}

/**
 * Reusable bearer-JSON axios-style client adapter. Returns an object whose
 * `get/post/put` resolve to `{ data }` (mirroring axios' default JSON shape),
 * so legacy call sites that read `response.data` keep working unchanged. The
 * actual I/O is routed through `apiFetch` (S3/CDN fallback, unified errors,
 * retry). Token is injected as a Bearer header; `credentials: 'omit'`.
 *
 * Errors preserve `error.response.status` (a Response object) so existing
 * `error?.response?.status === 404` fallback chains continue to work — see the
 * rate-limit guard in the 4xx handler below.
 */
export function createBearerJsonClient(token, { timeoutMs = 60_000, baseURL } = {}) {
  const base = baseURL != null ? String(baseURL).replace(/\/$/, '') : API_URL;
  const run = async (method, path, bodyOrParams, maybeParams) => {
    // For GET: bodyOrParams is the query params (axios-style). For all other
    // methods: bodyOrParams is the request body and maybeParams (a 3rd arg of
    // `{ params }`) is the query params — also axios-style, e.g.
    //   api.put(url, payload, { params: { path: 'x' } })
    // Previously we only forwarded query params on GET, so non-GET callers
    // that passed `{ params }` (e.g. saveStructuredCourseText) silently lost
    // the query string and the server returned 400 "path is required".
    let body;
    let params;
    if (method === 'GET') {
      params = bodyOrParams;
    } else {
      body = bodyOrParams;
      params = maybeParams;
    }
    const url = buildUrl(`${base}${path}`, params);
    const opts = {
      method,
      token,
      timeoutMs,
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    };
    if (method !== 'GET' && body !== undefined) opts.body = body;
    const data = await apiFetch(url, opts);
    return { data, status: 200, statusText: 'OK' };
  };

  const deleteImpl = (path, bodyOrOptions) => {
    let body;
    let params;
    if (bodyOrOptions && typeof bodyOrOptions === 'object' && !Array.isArray(bodyOrOptions)) {
      if (bodyOrOptions.data !== undefined) body = bodyOrOptions.data;
      if (bodyOrOptions.params !== undefined) params = bodyOrOptions.params;
      if (body === undefined && params === undefined) body = bodyOrOptions;
    } else {
      body = bodyOrOptions;
    }
    return run('DELETE', path, body, params);
  };

  return {
    get: (path, options = {}) => run('GET', path, options?.params),
    post: (path, body, options = {}) => run('POST', path, body, options?.params),
    put: (path, body, options = {}) => run('PUT', path, body, options?.params),
    patch: (path, body, options = {}) => run('PATCH', path, body, options?.params),
    // Mirrors axios' `axios.delete(url, { data })` — accepts a body directly
    // or the axios-style `{ data }` options object. Also accepts `{ params }`
    // so callers can pass query strings on DELETE.
    del: (path, bodyOrOptions) => deleteImpl(path, bodyOrOptions),
    delete: (path, bodyOrOptions) => deleteImpl(path, bodyOrOptions),
  };
}

/**
 * Unified fetch client.
 *
 * @param {string} url
 * @param {object} [options]
 * @param {string} [options.method='GET']
 * @param {object} [options.headers]
 * @param {*}       [options.body]            auto JSON.stringified unless FormData/Blob/string/Buffer
 * @param {string} [options.token]            explicit bearer token (overrides provider)
 * @param {number} [options.timeoutMs=DEFAULT_API_TIMEOUT_MS] 0 disables
 * @param {string} [options.credentials='include']
 * @param {number} [options.retryNetwork=1]  retry count on network failures (max 2)
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.rawResponse=false] if true, return the Response on a
 *   tolerated status (2xx, or any code in `acceptedStatuses`) instead of parsing.
 *   Use this when the caller must inspect the status itself (e.g. 304/ETag
 *   conditional GET, or a 404 that should be treated as "not present" rather
 *   than an error).
 * @param {number[]} [options.acceptedStatuses] non-2xx statuses that are NOT
 *   treated as errors. ONLY honoured when `rawResponse: true` — the caller
 *   receives the Response and inspects the status itself (e.g. 304 ETag,
 *   404 "absent"). With `rawResponse: false` (parsed mode) a non-2xx is ALWAYS
 *   an ApiError, since there is no way to surface the status in a data return.
 *   NOTE: 429/503 are ALWAYS surfaced as rate-limit ApiErrors regardless — they
 *   are handled before this set is consulted.
 * @returns {Promise<*>} parsed JSON/text on success, or the Response when rawResponse
 */
export async function apiFetch(url, options = {}) {
  const {
    method = 'GET',
    headers: rawHeaders = {},
    body,
    token,
    timeoutMs = DEFAULT_API_TIMEOUT_MS,
    credentials = 'include',
    retryNetwork = 1,
    signal,
    rawResponse = false,
    acceptedStatuses = null,
    cache,
  } = options;

  const headers = { ...rawHeaders };
  if (
    body !== undefined &&
    body !== null &&
    !(body instanceof FormData) &&
    headers['Content-Type'] == null &&
    headers['content-type'] == null
  ) {
    headers['Content-Type'] = 'application/json';
  }
  const resolvedToken = resolveApiToken(token);
  if (resolvedToken) {
    headers.Authorization = `Bearer ${resolvedToken}`;
  }
  // Auto-attach a captcha token for the heavy-media endpoints that the backend
  // gates with Turnstile. getTurnstileToken() returns null when captcha is
  // disabled, so non-captcha and pre-rollout traffic is unaffected.
  if (CAPTCHAED_PATHS.some((p) => String(url).includes(p))) {
    const tk = await getTurnstileToken();
    if (tk) headers['x-turnstile-token'] = tk;
  }

  const controller = timeoutMs > 0 && !signal ? new AbortController() : null;
  let timer = null;
  if (controller) {
    timer = setTimeout(() => controller.abort(), timeoutMs);
  }
  const finalSignal = signal || (controller ? controller.signal : undefined);

  const maxAttempts = 1 + Math.max(0, Math.min(2, retryNetwork));
  let attempt = 0;
  let lastErr;
  while (attempt < maxAttempts) {
    attempt += 1;
    try {
      // For GET, omit `method` entirely — fetch defaults to GET, and the legacy
      // request helpers' tests assert the fetch init carries no explicit method
      // for GET. Non-GET keeps the method (required).
      const fetchInit = {
        headers,
        body: normaliseBody(body),
        credentials,
        cache,
        signal: finalSignal,
      };
      if (method !== 'GET') fetchInit.method = method;
      const res = await fetch(url, fetchInit);
      if (timer) clearTimeout(timer);

      if (isRateLimitedStatus(res.status)) {
        const retryAfter = parseRetryAfterSec(res) || 3600;
        const err = new ApiError(
          res.status === 503
            ? `Service temporarily suspended. Retry after ${retryAfter}s.`
            : `Rate limit exceeded. Retry after ${retryAfter}s.`,
          { status: res.status, statusText: res.statusText, url, retryAfterSec: retryAfter },
        );
        // Expose the Response so legacy callers that read `error.response.status`
        // (e.g. 429/503 rate-limit detection) keep working — axios always set it.
        err.response = res;
        throw err;
      }

      // `acceptedStatuses` only has effect with rawResponse:true — a non-2xx
      // must be surfaced as an ApiError when we return parsed data, otherwise the
      // caller has no way to observe that the status was "accepted".
      const isAccepted =
        rawResponse && !res.ok && Array.isArray(acceptedStatuses) && acceptedStatuses.includes(res.status);

      if (rawResponse) {
        // Return the Response for any tolerated status (2xx or accepted) so the
        // caller can inspect status / headers (e.g. 304 ETag, 404 "absent").
        if (res.ok || isAccepted) {
          return res;
        }
        const err = new ApiError(`HTTP ${res.status} for ${url}`, {
          status: res.status,
          statusText: res.statusText,
          url,
        });
        err.response = res;
        throw err;
      }

      // Non-raw: parse the body. An accepted non-2xx (e.g. 304) has no body, so
      // the best-effort parse returns '' — callers needing the status should use
      // rawResponse:true instead.
      if (!res.ok && !isAccepted) {
        let bodyText = '';
        try {
          bodyText = await res.text();
        } catch {
          /* ignore */
        }
        let bodyData = bodyText;
        try {
          if (bodyText && looksLikeJson(bodyText)) bodyData = JSON.parse(bodyText);
        } catch {
          /* keep raw text */
        }
        const err = new ApiError(`HTTP ${res.status} for ${url}`, {
          status: res.status,
          statusText: res.statusText,
          url,
          body: bodyText,
        });
        // Mirror axios' `error.response` shape so legacy handlers that read
        // `error.response?.data?.message` keep working after converging off axios.
        err.response = res;
        err.response.data = bodyData;
        throw err;
      }

      const ct = res.headers?.get?.('content-type') || '';
      if (ct.includes('application/json')) {
        return typeof res.json === 'function' ? await res.json() : '';
      }
      // Some mocked responses only implement one of json/text — tolerate both.
      let text = '';
      if (typeof res.text === 'function') {
        text = await res.text();
      } else if (typeof res.json === 'function') {
        try {
          const j = await res.json();
          text = typeof j === 'string' ? j : JSON.stringify(j);
        } catch {
          text = '';
        }
      }
      // A JSON-looking body (even with a wrong content-type) is still parsed;
      // this also stops HTML error pages from being force-parsed as JSON.
      if (looksLikeJson(text)) {
        try {
          return JSON.parse(text);
        } catch {
          /* fall through to raw text */
        }
      }
      return text;
    } catch (e) {
      if (timer) clearTimeout(timer);
      lastErr = e;
      // HTTP / rate-limit / abort errors are thrown as-is (no blind retry).
      if (e instanceof ApiError) throw e;
      if (e && e.name === 'AbortError') throw e;
      if (attempt < maxAttempts) continue;
      throw new ApiError(lastErr?.message || 'Network request failed', { url, body: lastErr });
    }
  }
  throw lastErr;
}

/** Convenience alias: apiFetch already parses JSON, this documents intent. */
export function apiFetchJson(url, options = {}) {
  return apiFetch(url, { ...options, rawResponse: false });
}

/** Fetch plain text (never parsed as JSON). */
export function apiFetchText(url, options = {}) {
  return apiFetch(url, { ...options, rawResponse: false }).then((r) =>
    typeof r === 'string' ? r : '',
  );
}
